import {noop} from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {Pill} from '../../../../../libs/shared/ui/src/components/pill/pill';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {ReportSectionMetricSelectionDropdownLabel} from '../reportSection/reportSectionMetricSelectionDropdownLabel';
import {TableColumnPickerProps} from './tableColumnPicker';

// TODO(ldemarne): DRY with `ReportSectionMetricSelectorDropdown`.

/*
 * Props.
 */

export type ColumnIconNames<TColumnId extends string> = Record<TColumnId, IconName>;

interface TableColumnPickerWithNamesProps<TColumnId extends string>
  extends TableColumnPickerProps<TColumnId> {
  columnIconNames?: ColumnIconNames<TColumnId>;
}

/*
 * Styles.
 */

const StyledPrimePill = styled(Pill)`
  margin-left: 4px;
`;

const StyledPlaceholderSpan = styled.span`
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const TableColumnPickerWithNames = <TColumnId extends string>(
  props: TableColumnPickerWithNamesProps<TColumnId>,
) => (
  <DropdownBoxCoordinator
    isDisabled={props.isLoading}
    value={renderDropdownButtonLabel(props)}
    renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
    size={VisualSizesEnum.SMALL}
    shouldDisablePadding
  />
);

const renderDropdownButtonLabel = <TColumnId extends string>(props: TableColumnPickerProps<TColumnId>) => {
  const {canBeHiddenColumnIds, hiddenColumnIds, columnNames} = props;
  const selectedColumnIds = canBeHiddenColumnIds.filter((columnId) => !hiddenColumnIds.includes(columnId));

  if (selectedColumnIds.length === 0) {
    return (
      <StyledPlaceholderSpan>
        <Trans id="analytics-select-metrics-button">Select metrics</Trans>
      </StyledPlaceholderSpan>
    );
  }

  const selectedColumnNames = selectedColumnIds.map((id) => columnNames[id]);
  return <ReportSectionMetricSelectionDropdownLabel selectedMetricLabels={selectedColumnNames} />;
};

function renderDropdown<TColumnId extends string>(
  popoverTransferProps: PopoverTransferProps,
  props: TableColumnPickerWithNamesProps<TColumnId>,
) {
  const {
    canBeHiddenColumnIds,
    hiddenColumnIds,
    disabledColumnIds,
    columnNames,
    columnIconNames,
    onColumnClick,
  } = props;

  return (
    <Dropdown {...popoverTransferProps} width={250}>
      {/* onRequestClose prop is used to keep the menu open when an item is clicked */}
      <DropdownList {...popoverTransferProps} onRequestClose={noop}>
        {canBeHiddenColumnIds.map((columnId) => {
          const isChecked = !hiddenColumnIds.includes(columnId);
          const isDisabled = disabledColumnIds.includes(columnId);
          const columnName = columnNames[columnId];
          const iconName = columnIconNames?.[columnId];

          return (
            <DropdownItem
              key={columnId}
              isChecked={isChecked}
              onSelect={() => onColumnClick(columnId)}
              shouldHideCheckmark
              isDisabled={isDisabled}
            >
              {iconName && <DropdownItemIcon label={columnName} iconName={iconName} />}
              <DropdownItemContent isChecked={isChecked}>
                {columnName}
                {isDisabled && (
                  <StyledPrimePill size={VisualSizesEnum.MEDIUM}>
                    <Trans id="analytics-upsell-table-col-picker-growth">GROWTH</Trans>
                  </StyledPrimePill>
                )}
              </DropdownItemContent>
              <DropdownItemCheckbox isChecked={isChecked} />
            </DropdownItem>
          );
        })}
      </DropdownList>
    </Dropdown>
  );
}
